import moment from 'moment';
import * as React from 'react';
import { Button, Popover, PopoverBody } from 'reactstrap';
import { mergeClasses } from '../constants';
import { StartEndDateFunction } from '../types';
import { TOOLTIP_DELAY_WAIT_SHOW_INSTANT_HIDE } from './LightTooltip';
import LocalDate from './LocalDate';
import './TimeSelectionDropdown.scss';
import { Placement } from '@popperjs/core';

type customRangeWidgetProps = {
  onChange: StartEndDateFunction;
  earliest: moment.Moment;
  maxRangeDays: number;
  start?: moment.Moment;
  end?: moment.Moment;
};

type Props = {
  start: moment.Moment;
  end: moment.Moment;
  earliest: moment.Moment;
  maxRangeDays: number;
  onChange: StartEndDateFunction;
  disabled?: boolean;
  placement?: 'left' | 'right';
  customRangeWidget?: React.ComponentType<customRangeWidgetProps>;
};

type State = {
  isSelectorOpen: boolean;
  isSelectorCustom: boolean;
};

let idCounter = 0;

const buttons = [
  { label: 'Past Hour', hours: 1 },
  { label: 'Past 24 Hours', hours: 24 },
  { label: 'Past 72 Hours', hours: 72 },
  { label: 'Past Week', hours: 168 },
];

export default class TimeSelectionDropdown extends React.Component<
  Props,
  State
> {
  id: string;

  constructor(props: Props) {
    super(props);
    this.state = {
      isSelectorOpen: false,
      isSelectorCustom: false,
    };
    this.id = `trp_${++idCounter}`;
  }

  quickFilter = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    const amount = Number(event.currentTarget.getAttribute('name'));
    if (!isNaN(amount)) {
      const end = moment(),
        start = end.clone().subtract(amount, 'hours');
      this.props.onChange(start, end);
      this.setState({ isSelectorOpen: false, isSelectorCustom: false });
    }
  };

  toggle = () => {
    this.setState((prev: State) => ({
      isSelectorOpen: !prev.isSelectorOpen && !this.props.disabled,
      isSelectorCustom: prev.isSelectorCustom && !prev.isSelectorOpen, // revert to 'non-custom' on closing
    }));
  };

  applyCustomRange = (start: moment.Moment, end: moment.Moment) => {
    this.setState({
      isSelectorOpen: false,
      isSelectorCustom: false,
    });
    this.props.onChange(start, end);
  };

  toggleCustomRange = () => {
    this.setState((prev: State) => ({
      isSelectorCustom: !prev.isSelectorCustom,
    }));
  };

  render() {
    const {
      customRangeWidget: CustomRange,
      disabled = false,
      start,
      end,
      earliest,
      maxRangeDays,
      placement = 'left',
    } = this.props;
    const isSelectorOpen = this.state.isSelectorOpen && !disabled,
      isSelectorCustom = this.state.isSelectorCustom;
    let position: { placement: Placement; offset?: number } = {
      placement: placement === 'left' ? 'bottom-end' : 'bottom-start',
    };
    if (placement === 'right') {
      position.offset = 53;
    }
    const customRange =
      isSelectorCustom && CustomRange ? (
        <CustomRange
          start={start}
          end={end}
          earliest={earliest}
          onChange={this.applyCustomRange}
          maxRangeDays={maxRangeDays}
        />
      ) : null;

    return (
      <div className="time-selection-dropdown">
        <div
          id={this.id}
          className={mergeClasses([
            'form-control',
            disabled ? 'disabled' : null,
          ])}
          style={{ minWidth: '19em' }}
          onClick={this.toggle}
          onKeyPress={this.toggle}
          tabIndex={0}
        >
          <div className="mx-1">
            <LocalDate
              date={start}
              delay={TOOLTIP_DELAY_WAIT_SHOW_INSTANT_HIDE}
              placement="top"
            />
            <span className="separator">to</span>
            <LocalDate
              date={end}
              delay={TOOLTIP_DELAY_WAIT_SHOW_INSTANT_HIDE}
              placement="top"
            />
          </div>
        </div>

        <Popover
          className="time-selection-dropdown"
          isOpen={isSelectorOpen}
          {...position}
          target={this.id}
          toggle={this.toggle}
          key={isSelectorCustom ? 'custom' : 'normal'}
          fade={false}
          trigger="legacy"
          modifiers={{
            flip: {
              enabled: false,
            },
          }}
        >
          <PopoverBody>
            {placement === 'left' && customRange}
            <div className="d-flex flex-column pt-2">
              {buttons
                .filter((button) => button.hours <= maxRangeDays * 24)
                .map((button) => (
                  <Button
                    key={button.hours}
                    color="light"
                    size="sm"
                    name={`${button.hours}`}
                    onClick={this.quickFilter}
                  >
                    {button.label}
                  </Button>
                ))}
              {CustomRange ? (
                <Button
                  data-cy="custom-range"
                  color="light"
                  size="sm"
                  onClick={this.toggleCustomRange}
                  active={isSelectorCustom}
                >
                  Custom Range
                </Button>
              ) : null}
            </div>
            {placement === 'right' && customRange}
          </PopoverBody>
        </Popover>
      </div>
    );
  }
}
